import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { safeSetInterval, safeClearInterval } from '../../../utils/intervalManager';
import { getAppointmentsByFilter } from '../../appointment/appointmentFunctions';

export const useStaffAppointmentData = (selectedService, activeFilters = null) => {
    const dispatch = useDispatch();
    const practiceDetails = useSelector(state => state.practiceDetails);
    const loginDetails = useSelector(state => state.loginDetails);
    const systemConstants = useSelector(state => state.systemConstants?.constants);
    
    const practice = practiceDetails?.practice;
    const token = loginDetails?.jwtToken || "";
    
    // Track initialization and interval to prevent duplicates
    const isInitializingRef = useRef(false);
    const intervalRef = useRef(null);
    
    // Get service IDs from practice
    const serviceIds = useMemo(() => {
        const ids = [];
        if (practice && practice.practice_service) {
            practice.practice_service.forEach(pServ => {
                if (!pServ.is_deleted) {
                    ids.push(pServ._id);
                }
            });
        }
        return ids;
    }, [practice]);

    // Helper function to get default date range (last 30 days)
    const getDefaultDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30); // 30 days ago
        
        return {
            start: startDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
            end: endDate.toISOString().split('T')[0]
        };
    };

    // Unified refresh function that uses filter API for both periodic refresh and manual filtering
    const refreshAppointments = () => {
        if (!selectedService || !token) {
            return;
        }

        
        // If there are active filters, use the filter API
        if (activeFilters && activeFilters.type === 'combined') {
            getAppointmentsByFilter(selectedService, dispatch, token, activeFilters, systemConstants);
        } else {
            // Create a default filter with last 30 days to satisfy backend validation
            const defaultDateRange = getDefaultDateRange();
            const defaultFilter = {
                type: 'combined',
                value: {
                    date: {
                        type: 'date',
                        value: defaultDateRange
                    }
                }
            };
            getAppointmentsByFilter(selectedService, dispatch, token, defaultFilter, systemConstants);
        }
    };

    // Set up unified polling for appointment data
    useEffect(() => {
        const intervalDescription = 'Staff Appointments Unified Poll';
        
        // Only poll if we have service IDs and a selected service
        if (selectedService && token && serviceIds.length > 0 && !isInitializingRef.current) {
            isInitializingRef.current = true;
            
            // Clear any existing interval first
            if (intervalRef.current) {
                safeClearInterval(intervalRef.current, intervalDescription);
                intervalRef.current = null;
            }
            
            // Fallback: If no service is selected, auto-select the first one
            if (!selectedService && serviceIds.length > 0) {
                dispatch({
                    type: 'selectServiceForAppointments',
                    payload: serviceIds[0]
                });
            }
            
            // Initial refresh using unified filter API
            refreshAppointments();
            
            // Set up unified polling every 30 seconds using the same filter API
            intervalRef.current = safeSetInterval(() => {
                console.log('=== PERIODIC REFRESH (30s) ===');
                refreshAppointments();
            }, 30000, intervalDescription);
        }

        // Cleanup interval on unmount or dependency change
        return () => {
            if (intervalRef.current) {
                safeClearInterval(intervalRef.current, intervalDescription);
                intervalRef.current = null;
            }
            isInitializingRef.current = false;
        };
    }, [serviceIds.length, token, dispatch, selectedService, serviceIds, activeFilters, systemConstants]);
}
